/**
 * Domain configuration for the marketing/app split.
 *
 * Marketing domain (lexgat.com) serves public pages.
 * App domain (app.lexgat.com) serves authenticated app routes.
 */

export const MARKETING_DOMAIN = 'lexgat.com';
export const APP_DOMAIN = 'app.lexgat.com';

export const MARKETING_URL = `https://${MARKETING_DOMAIN}`;
export const APP_URL = `https://${APP_DOMAIN}`;

// Paths that belong on the marketing domain.
export const MARKETING_PATHS = [
  '/',
  '/how-it-works',
  '/about',
  '/contact',
  '/pricing',
  '/privacy-policy',
  '/terms-conditions',
  '/statutes',
];

// Paths that belong on the app domain.
// When adding a route here, also update Disallow entries in public/robots.txt.
export const APP_PATHS = [
  '/login',
  '/onboarding',
  '/ip-protection-agreement',
  '/intellectual-property-notice',
  '/acknowledgement',
  '/affidavit',
  '/complete-profile',
  '/join',
  '/dashboard',
  '/cases',
  '/closed-cases',
  '/drafts',
  '/uploads',
  '/import-old-cases',
  '/recycle-bin',
  '/library',
  '/diary',
  '/analytics',
  '/workspace',
  '/settings',
  '/team',
  '/admin',
] as const;

/**
 * App paths that should refresh in place (not redirect to /dashboard).
 * Kept in sync with APP_PATHS — used by SessionRestorer in App.tsx.
 */
export const APP_REFRESH_EXEMPT_PREFIXES: readonly string[] = APP_PATHS;

/**
 * Does this path stay on the current URL after a browser refresh on the app domain?
 */
export function isRefreshExemptPath(pathname: string): boolean {
  return (
    pathname === '/' ||
    APP_REFRESH_EXEMPT_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
    )
  );
}

/**
 * Development hosts: localhost, 127.0.0.1, and LAN IPs. On these we skip
 * cross-domain redirects entirely since both route groups share one host.
 */
export function isLocalhost(hostname: string = window.location.hostname): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname === '::1' ||
    hostname.startsWith('192.168.')
  );
}

/**
 * Is the current host the app domain? Anything starting with `app.` is treated
 * as the app domain; everything else (including localhost) is marketing.
 */
export function isAppHost(hostname: string = window.location.hostname): boolean {
  if (hostname === APP_DOMAIN) return true;
  if (hostname.startsWith('app.')) return true;
  return false;
}

/**
 * Is the current host the marketing domain?
 */
export function isMarketingHost(hostname: string = window.location.hostname): boolean {
  return !isAppHost(hostname);
}

/**
 * Does this path belong on the marketing domain?
 */
export function isMarketingPath(pathname: string): boolean {
  return MARKETING_PATHS.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
  );
}

/**
 * Does this path belong on the app domain?
 */
export function isAppPath(pathname: string): boolean {
  return APP_PATHS.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
  );
}

/**
 * Build an absolute URL on the marketing domain, preserving path/query/hash.
 * On localhost, returns a relative path so dev stays on localhost.
 */
export function marketingUrl(pathname: string, search = '', hash = ''): string {
  if (isLocalhost()) return `${pathname}${search}${hash}`;
  return `${MARKETING_URL}${pathname}${search}${hash}`;
}

/**
 * Build an absolute URL on the app domain, preserving path/query/hash.
 * On localhost, returns a relative path so dev stays on localhost.
 */
export function appUrl(pathname: string, search = '', hash = ''): string {
  if (isLocalhost()) return `${pathname}${search}${hash}`;
  return `${APP_URL}${pathname}${search}${hash}`;
}
